import * as vp from 'vopay';
import {
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  formattedCurrency,
  formatMaskedAccount,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useHostNavigate } from '../HostNavigationContext';
import useFetchScheduledPayments, { toScheduledPaymentsFilters } from '../hooks/useFetchScheduledPayments';
import { useI18nLabels } from '../hooks/useI18nLabels';
import ScheduledTransactionsForm from './ScheduledTransactionsForm';
import ActivityType, { ActivityTabType } from './ActivityType';
import type { ActivitySubTableProps } from './TransactionHistoryTable';
import styles from './ScheduledTransactionHistoryTable.module.scss';

import { Skeleton, Table, TableFiltersContainer, TransactionStatus } from '#shared/components/UI';
import toDayTimestamp from './toDayTimestamp';

type QueryParams = {
  status?: string;
  scheduleType?: string;
  transactionType?: string;
  frequency?: vp.NameOfFrequency | 'all';
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  activityType?: ActivityTabType;
};

// URL query params are plain strings; this narrows one back to the frequency
// union cast-free (an unknown value falls back to '').
const FREQUENCY_VALUES: (vp.NameOfFrequency | 'all')[] = [
  'all',
  'daily',
  'weekly',
  'bi-weekly',
  'monthly',
  'semi-monthly',
  'quarterly',
  'semi-annually',
  'annually',
  'weekdays',
  'custom',
];

function toFrequency(value: string | null): vp.NameOfFrequency | 'all' | '' {
  return FREQUENCY_VALUES.find((frequency) => frequency === value) ?? '';
}

type Filters = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  frequency: vp.NameOfFrequency | 'all' | '';
  scheduleType: string;
  transactionType: string;
};

type TransactionParams = Filters & {
  hasInvalidEndDate: boolean;
  offset?: number;
};

// The portal's Customer Activity → Scheduled Transactions table, adapted for
// the contact drawer/portal pages: filters live in memory (mirrored to the
// URL when persistFiltersInUrl) and the schedule-details link-out goes
// through the host's onNavigate (plain text when the host can't navigate).
const ScheduledTransactionHistoryTable: FunctionComponent<ActivitySubTableProps> = ({
  clientAccountId,
  country,
  activityTypeOptions,
  currentType,
  handleCurrentType,
  persistFiltersInUrl,
}) => {
  const alertDialog = useAlertDialog();
  const hostNavigate = useHostNavigate();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ScheduledTransactionsHistory' });
  const { frequencyLabelMapper, paymentTypeLabelMapper, scheduledPaymentStatusLabelMapper } = useI18nLabels();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.MergedScheduledPayment[]>([]);
  const [numTotalRecords, setNumTotalRecords] = useState(0);
  const { fetching, fetchScheduledPayments } = useFetchScheduledPayments(country);

  const initialFilters: Filters = {
    status: '',
    searchTerm: '',
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    frequency: '',
    scheduleType: '',
    transactionType: '',
  };

  // Portal mode seeds the filters from the page URL (deep links, reload/back
  // restoration); the drawer starts from the in-memory defaults.
  const [filters, setFilters] = useState<Filters>(() => {
    if (!persistFiltersInUrl) return initialFilters;

    const urlParams = new URLSearchParams(window.location.search);

    return {
      status: urlParams.get('status') || '',
      searchTerm: urlParams.get('searchTerm') || '',
      startDate: urlParams.get('startDate') || initialFilters.startDate,
      endDate: urlParams.get('endDate') || initialFilters.endDate,
      frequency: toFrequency(urlParams.get('frequency')),
      scheduleType: urlParams.get('scheduleType') || '',
      transactionType: urlParams.get('transactionType') || '',
    };
  });

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const filtersApplied = Boolean(
    filters.status ||
      filters.searchTerm ||
      filters.frequency ||
      filters.transactionType ||
      filters.scheduleType ||
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate,
  );

  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(numTotalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchScheduledTransactionHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    frequency,
    scheduleType,
    transactionType,
    offset = 0,
  }: TransactionParams) => {
    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    const request: vp.ScheduledPaymentsRequest = {
      StartDate: startDate,
      EndDate: endDate,
      SearchText: searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
      // The legacy endpoint reads ClientAccountID ?? ContactID into the same
      // filter, so contact-based accounts are covered by ClientAccountID too
      ClientAccountID: clientAccountId,
      ...toScheduledPaymentsFilters({ scheduleType, transactionType, frequency, status }),
    };

    try {
      const res = await fetchScheduledPayments(request);
      setFilteredList(res.scheduledPayments);
      setNumTotalRecords(res.numTotalRecords);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('ErrorMessages.fetchTransactions', {
              defaultValue: 'An error occurred while fetching scheduled transactions history.',
            });

      alertDialog.error(t('ErrorMessages.error', { defaultValue: 'Error' }), msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);

    fetchScheduledTransactionHistory({
      ...filters,
      hasInvalidEndDate: false,
      offset,
    });

    if (persistFiltersInUrl) {
      updateQueryParams({
        activityType: 'scheduledTransactions',
      });
    }
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(filters.startDate > filters.endDate);
  }, [filters.startDate, filters.endDate]);

  const tableProps: vp.TableProps<vp.MergedScheduledPayment> = {
    id: 'sheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'id',
        header: t('Columns.id', { defaultValue: 'ID' }),
        align: 'left',
        template: ({ data }) => {
          return (
            <>
              {/* Link-out via the host's onNavigate; plain text when the host can't navigate. */}
              {hostNavigate ? (
                <button
                  type="button"
                  className={styles.linkOut}
                  onClick={() =>
                    hostNavigate(`/scheduled-transactions/details/${data.ID}`, {
                      transactionId: data.ID,
                      // Legacy and new IDs are independent auto-increments; the
                      // details page uses this to query the right source
                      isLegacy: data.IsLegacy,
                    })
                  }
                >
                  {data.ID}
                </button>
              ) : (
                <span>{data.ID}</span>
              )}
            </>
          );
        },
      },
      {
        id: 'name',
        header: t('Columns.name', { defaultValue: 'Name' }),
        align: 'left',
        template: ({ data }) => data.TokenData.FullName || '',
      },
      {
        id: 'scheduleType',
        header: t('Columns.scheduleType', { defaultValue: 'Schedule Type' }),
        align: 'left',
        template: ({ data }) =>
          t(`SearchForm.Options.ScheduleTypeOptions.${data.ScheduleType}`, {
            defaultValue: capitalizeFirstLetterOfWords(data.ScheduleType),
          }),
      },
      {
        id: 'transactionType',
        header: t('Columns.transactionType', { defaultValue: 'Transaction Type' }),
        align: 'left',
        template: ({ data }) => paymentTypeLabelMapper(data.PaymentType),
      },
      {
        id: 'frequency',
        header: t('Columns.frequency', { defaultValue: 'Frequency' }),
        align: 'left',
        template: ({ data }) =>
          data.Repeats ? frequencyLabelMapper(data.Repeats) || capitalizeFirstLetterOfWords(data.Repeats) : '',
      },
      {
        id: 'amount',
        header: t('Columns.amount', { defaultValue: 'Amount' }),
        align: 'left',
        template: ({ data }) => (
          <>
            ${formattedCurrency(data.Amount)} {country === 'CA' ? 'CAD' : 'USD'}
          </>
        ),
      },
      {
        id: 'startDate',
        header: t('Columns.startDate', { defaultValue: 'Start Date' }),
        align: 'left',
        // Date-only: new rows carry a datetime while legacy rows carry a date,
        // so the raw values would render inconsistently in one column
        template: ({ data }) => data.StartDateTime.slice(0, 10),
      },
      {
        id: 'ending',
        header: t('Columns.ending', { defaultValue: 'Ending' }),
        align: 'left',
        template: ({ data }) =>
          data.ScheduleEndDate
            ? data.ScheduleEndDate
            : data.NumberOfPayments !== ''
              ? t('endingAfterPayments', {
                  defaultValue: 'Ending After {{counts}} Payments',
                  counts: data.NumberOfPayments,
                })
              : '',
      },
      {
        id: 'account',
        header: t('Columns.account', { defaultValue: 'Account' }),
        align: 'left',
        template: ({ data }) =>
          formatMaskedAccount(data.TokenData.MaskedAccount || data.TokenData.CreditCardNumber || ''),
      },
      {
        id: 'status',
        header: t('Columns.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status)}
              label={scheduledPaymentStatusLabelMapper(data.Status)}
            />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.MergedScheduledPayment } => ({
      // Source-prefixed so a legacy/new ID collision can't duplicate row keys
      id: item.IsLegacy ? `legacy-${item.ID}` : String(item.ID || ''),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />

          <TableFiltersContainer.Trigger />
        </Box>

        <TableFiltersContainer.Content>
          <ScheduledTransactionsForm
            timeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
            scheduleType={filters.scheduleType}
            frequency={filters.frequency}
            status={filters.status}
            searchTerm={filters.searchTerm}
            transactionType={filters.transactionType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            initialTimeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
            search={(searchParams) => {
              const next: Filters = {
                status: searchParams.status,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                scheduleType: searchParams.scheduleType,
                transactionType: searchParams.transactionType,
                frequency: searchParams.frequency,
              };

              if (persistFiltersInUrl) {
                updateQueryParams({
                  status: next.status,
                  searchTerm: next.searchTerm,
                  startDate: next.startDate,
                  endDate: next.endDate,
                  scheduleType: next.scheduleType,
                  transactionType: next.transactionType,
                  frequency: next.frequency || undefined,
                });
              }

              setFilters(next);

              fetchScheduledTransactionHistory({
                ...next,
                hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                offset: setPageIndex(0),
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>{fetching ? <Skeleton height="200px" /> : <Table {...tableProps} />}</div>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);

              fetchScheduledTransactionHistory({
                ...filters,
                hasInvalidEndDate: filters.startDate > filters.endDate,
                offset,
              });
            }}
          />
        )}
      </Box>

      {!fetching && filteredList.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('noRecordMessage', { defaultValue: 'There are no records to display within this date range.' })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledTransactionHistoryTable;
